import React, { useRef, useEffect } from "react";
import { WEATHER_RENDERER_COLORS } from "../theme";

// 上部ペイン: 天候の描画コンポーネント（3D透視投影版）
const WeatherRenderer = ({ data, width, height }) => {
  const canvasRef = useRef(null);
  const cloudsRef = useRef([]);
  const raindropsRef = useRef([]);

  // 透視投影のパラメータ
  const vanishingPointY = height * 0.4; // 消失点のY座標
  const horizonY = height * 0.5; // 地平線のY座標

  // 天候エフェクト（雲、雨粒）の初期化（3D風の配置）
  useEffect(() => {
    // 雲の初期化（晴れでも少し雲を表示）
    const cloudCount = data.weather === "sunny" ? 1 : 5;
    cloudsRef.current = Array(cloudCount)
      .fill(null)
      .map(() => ({
        x: (Math.random() - 0.5) * width * 1.5, // 中心からの相対位置
        y: Math.random() * height * 0.6 - height * 0.3, // 高さ（さらに広範囲に）
        z: Math.random() * 100, // 奥行き（0が手前、100が奥）
        baseSize: (Math.random() * 80 + 40) * 8, // 基本サイズ
        speed: Math.random() * 0.1 + 0.2, // 速度係数
      }));

    // 雨粒の初期化（3D配置）
    raindropsRef.current = Array(200)
      .fill(null)
      .map(() => ({
        x: Math.random() * width - width / 2,
        y: Math.random() * height - height * 0.5,
        z: Math.random() * 100,
        speed: Math.random() * 2 + 3,
      }));
  }, [width, height, data.weather]);

  // 描画ループ
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    // 背景色（グラデーション）
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    const bgColors = WEATHER_RENDERER_COLORS.background[data.weather];
    gradient.addColorStop(0, bgColors.top);
    gradient.addColorStop(1, bgColors.bottom);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // ハンドルの描画（下部1/3が画面外）
    const handleCenterX = width / 2;
    const handleRadius = Math.min(width, height) * 0.15;
    // ハンドルの中心を画面外に配置（半径の2/3だけ上に表示）
    const handleCenterY = height + handleRadius * 0.3;

    // クリッピング領域を設定（画面内のみ描画）
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, height);
    ctx.clip();

    // ハンドルの外側リング（黒）
    ctx.strokeStyle = WEATHER_RENDERER_COLORS.handle.outer;
    ctx.lineWidth = handleRadius * 0.3;
    ctx.beginPath();
    ctx.arc(handleCenterX, handleCenterY, handleRadius, 0, Math.PI * 2);
    ctx.stroke();

    // ハンドルの内側リング（グレー）
    ctx.strokeStyle = WEATHER_RENDERER_COLORS.handle.inner;
    ctx.lineWidth = handleRadius * 0.25;
    ctx.beginPath();
    ctx.arc(handleCenterX, handleCenterY, handleRadius, 0, Math.PI * 2);
    ctx.stroke();

    // ハンドルのハイライト（上部）
    ctx.strokeStyle = WEATHER_RENDERER_COLORS.handle.highlight;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(
      handleCenterX,
      handleCenterY,
      handleRadius - handleRadius * 0.125,
      Math.PI * 1.2,
      Math.PI * 1.8
    );
    ctx.stroke();

    // ハンドルの中央部分（センターハブ）
    ctx.fillStyle = WEATHER_RENDERER_COLORS.handle.center;
    ctx.beginPath();
    ctx.arc(handleCenterX, handleCenterY, handleRadius * 0.35, 0, Math.PI * 2);
    ctx.fill();

    // センターハブのロゴ風装飾
    ctx.fillStyle = WEATHER_RENDERER_COLORS.handle.centerLogo;
    ctx.beginPath();
    ctx.arc(handleCenterX, handleCenterY, handleRadius * 0.25, 0, Math.PI * 2);
    ctx.fill();

    // スポーク（ハンドルの支柱）- 3本スポーク
    ctx.strokeStyle = WEATHER_RENDERER_COLORS.handle.center;
    ctx.lineWidth = handleRadius * 0.15;

    // 左スポーク（斜め上向き）
    const leftAngle = Math.PI * 0.75; // 135度
    ctx.beginPath();
    ctx.moveTo(
      handleCenterX + Math.cos(leftAngle) * handleRadius * 0.3,
      handleCenterY + Math.sin(leftAngle) * handleRadius * 0.3
    );
    ctx.lineTo(
      handleCenterX + Math.cos(leftAngle) * handleRadius * 0.85,
      handleCenterY + Math.sin(leftAngle) * handleRadius * 0.85
    );
    ctx.stroke();

    // 右スポーク（斜め上向き）
    const rightAngle = Math.PI * 0.25; // 45度
    ctx.beginPath();
    ctx.moveTo(
      handleCenterX + Math.cos(rightAngle) * handleRadius * 0.3,
      handleCenterY + Math.sin(rightAngle) * handleRadius * 0.3
    );
    ctx.lineTo(
      handleCenterX + Math.cos(rightAngle) * handleRadius * 0.85,
      handleCenterY + Math.sin(rightAngle) * handleRadius * 0.85
    );
    ctx.stroke();

    // 下スポーク（画面内に見える場合のみ）
    if (handleCenterY - handleRadius * 0.85 < height) {
      ctx.beginPath();
      ctx.moveTo(handleCenterX, handleCenterY + handleRadius * 0.3);
      ctx.lineTo(handleCenterX, handleCenterY + handleRadius * 0.85);
      ctx.stroke();
    }

    // クリッピング領域を解除
    ctx.restore();

    // 太陽（晴れの場合）
    if (data.weather === "sunny") {
      const sunGradient = ctx.createRadialGradient(
        width - width * 0.1,
        height * 0.15,
        0,
        width - width * 0.1,
        height * 0.15,
        Math.min(width, height) * 0.08
      );
      sunGradient.addColorStop(0, WEATHER_RENDERER_COLORS.sun.center);
      sunGradient.addColorStop(0.5, WEATHER_RENDERER_COLORS.sun.middle);
      sunGradient.addColorStop(1, WEATHER_RENDERER_COLORS.sun.outer);
      ctx.fillStyle = sunGradient;
      ctx.beginPath();
      ctx.arc(
        width - width * 0.1,
        height * 0.15,
        Math.min(width, height) * 0.08,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }

    // 雲の更新と描画
    cloudsRef.current = cloudsRef.current.map((cloud) => {
      // 一時停止中は速度を大幅に減速（通常の5%）
      const currentSpeed = data.is_paused ? cloud.speed * 0.05 : cloud.speed;

      // Z軸方向の移動（手前に向かってくる）
      cloud.z -= currentSpeed;
      if (cloud.z < 0) {
        // 画面から出たら奥に戻す
        cloud.z = 100;
        cloud.x = (Math.random() - 0.5) * width * 1.5;
        cloud.y = Math.random() * height * 0.6 - height * 0.3;
      }

      // 透視投影の計算
      const scale = 1 - cloud.z / 100; // 0（奥）から1（手前）
      const projectedX = width / 2 + cloud.x * scale;
      const projectedY = vanishingPointY + (cloud.y - vanishingPointY) * scale;
      const projectedSize = cloud.baseSize * scale;

      // 雲の描画（奥ほど薄く、小さく）
      const opacity =
        data.weather === "sunny"
          ? 0.4 + scale * 0.3 // 晴れの日は薄めの雲（0.4-0.7）
          : 0.5 + scale * 0.3; // 曇り/雨の日は濃いめ（0.5-0.8）

      // 雲の色（天候に応じて変更）
      const cloudColorRGB = WEATHER_RENDERER_COLORS.cloud[data.weather];
      const cloudColor = `rgba(${cloudColorRGB[0]}, ${cloudColorRGB[1]}, ${cloudColorRGB[2]}, ${opacity})`;

      ctx.fillStyle = cloudColor;

      // 不規則な雲の形状を描画
      ctx.save();
      ctx.translate(projectedX, projectedY);
      ctx.rotate(cloud.rotation || 0);

      ctx.beginPath();

      // deformationsが存在しない場合はシンプルな楕円形
      if (!cloud.deformations || cloud.deformations.length === 0) {
        ctx.ellipse(
          0,
          0,
          projectedSize * 0.4,
          projectedSize * 0.2,
          0,
          0,
          Math.PI * 2
        );
        ctx.fill();
        ctx.restore();
        return cloud;
      }

      // ベジェ曲線で自然な雲の形を作成
      const points = [];
      for (let i = 0; i < cloud.deformations.length; i++) {
        const def = cloud.deformations[i];
        const angle = (i / cloud.deformations.length) * Math.PI * 2;
        const radius = projectedSize * def.radius * 0.4; // サイズに合わせて調整
        points.push({
          x: Math.cos(angle) * radius,
          y: Math.sin(angle) * radius * 0.5, // 縦方向はさらに圧縮
        });
      }

      // 最初の点に移動
      ctx.moveTo(points[0].x, points[0].y);

      // ベジェ曲線で滑らかに接続
      for (let i = 0; i < points.length; i++) {
        const current = points[i];
        const next = points[(i + 1) % points.length];
        const next2 = points[(i + 2) % points.length];

        // コントロールポイントを計算
        const cp1x = current.x + (next.x - current.x) * 0.5;
        const cp1y = current.y + (next.y - current.y) * 0.5;
        const cp2x = next.x - (next2.x - current.x) * 0.2;
        const cp2y = next.y - (next2.y - current.y) * 0.2;

        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, next.x, next.y);
      }

      ctx.closePath();
      ctx.fill();

      // 曇り/雨の時は内部に追加の陰影
      if (data.weather !== "sunny") {
        const shadowColorKey =
          data.weather === "cloudy" ? "shadowCloudy" : "shadowRainy";
        const shadowColorRGB = WEATHER_RENDERER_COLORS.cloud[shadowColorKey];
        ctx.fillStyle = `rgba(${shadowColorRGB[0]}, ${shadowColorRGB[1]}, ${
          shadowColorRGB[2]
        }, ${opacity * (data.weather === "cloudy" ? 0.2 : 0.3)})`;

        ctx.beginPath();
        for (let i = 0; i < points.length; i++) {
          const p = points[i];
          if (i === 0) {
            ctx.moveTo(p.x * 0.7, p.y * 0.7 + projectedSize * 0.05);
          } else {
            ctx.lineTo(p.x * 0.7, p.y * 0.7 + projectedSize * 0.05);
          }
        }
        ctx.closePath();
        ctx.fill();
      }

      ctx.restore();

      return cloud;
    });

    // 雨の描画（雨天時のみ）
    if (data.weather === "rainy") {
      raindropsRef.current = raindropsRef.current.map((drop) => {
        // 一時停止中でなければ雨粒を移動
        if (!data.is_paused) {
          drop.z -= drop.speed;
          if (drop.z < 0) {
            drop.z = 100;
            drop.x = (Math.random() - 0.5) * width * 2;
            drop.y = Math.random() * height - height * 0.5;
          }

          // 雨粒の落下
          drop.y += drop.speed * 3;
          if (drop.y > height) {
            drop.y = -50;
          }
        }

        const scale = 1 - drop.z / 100;
        const projectedX = width / 2 + drop.x * scale;
        const projectedY = drop.y;
        const length = 20 * scale;
        const opacity = 0.3 + scale * 0.4;

        const raindropRGB = WEATHER_RENDERER_COLORS.raindrop;
        ctx.strokeStyle = `rgba(${raindropRGB[0]}, ${raindropRGB[1]}, ${raindropRGB[2]}, ${opacity})`;
        ctx.lineWidth = Math.max(1, 3 * scale);
        ctx.beginPath();
        ctx.moveTo(projectedX, projectedY);
        ctx.lineTo(projectedX - 2 * scale, projectedY + length);
        ctx.stroke();

        // 雨粒の落下
        drop.y += drop.speed * 3;
        if (drop.y > height) {
          drop.y = -20;
        }

        return drop;
      });
    }
  }, [data, width, height, vanishingPointY]);

  return (
    <div className="relative w-full h-full">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="block w-full h-full"
      />
    </div>
  );
};

export default WeatherRenderer;
